import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Briefcase, Plus, PencilSimple, Trash, X, CheckCircle, XCircle, Megaphone, Handshake, ListChecks, UserCircle } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { fmtDateFR } from '../utils/dates';
import { TenderTrackingPanel } from '../components/opportunities/TenderTrackingPanel';
import { LayoutToggle, useLayoutMode, getLayoutContainerProps } from '../components/ui/LayoutToggle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPES = [
  { key: 'appel_offres', label: 'Appel d\'offre', icon: Megaphone, description: 'Appels d\'offres et affaires en négociation' },
];
const STATUS_CONFIG = {
  en_cours: { label: 'En cours', bg: '#E3F2FD', color: '#1565C0' },
  en_nego: { label: 'En négociation', bg: '#FFF3E0', color: '#EF6C00' },
  gagnee: { label: 'Gagnée', bg: '#E8F5E9', color: '#2E7D32' },
  perdue: { label: 'Perdue', bg: '#FFEBEE', color: '#C62828' },
};

const fmtDate = (iso) => { if (!iso) return '-'; try { return fmtDateFR(iso); } catch { return '-'; } };

const emptyForm = (type) => ({
  type, title: '', estimated_amount: 0, target_date: '', status: 'en_cours', notes: '',
  client_name: '', client_email: '', client_phone: '',
  organization: '', deadline: '', reference: '',
  source: '',
});

const OpportunitiesPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [activeType, setActiveType] = useState('appel_offres');
  const [items, setItems] = useState([]);
  const [layoutMode, setLayoutMode] = useLayoutMode('opportunities', 'grid');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm('appel_offres'));
  const [trackOpp, setTrackOpp] = useState(null);
  const [openCardId, setOpenCardId] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/opportunities`, { params: { type: activeType }, withCredentials: true });
      setItems(res.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [activeType]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openCreate = () => { setEditing(null); setForm(emptyForm(activeType)); setShowForm(true); };
  const openEdit = (o) => { setEditing(o); setForm({ ...emptyForm(o.type), ...o }); setShowForm(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await axios.put(`${API_URL}/api/opportunities/${editing.id}`, form, { withCredentials: true });
      else await axios.post(`${API_URL}/api/opportunities`, form, { withCredentials: true });
      toast.success(editing ? 'Mis a jour' : 'Cree');
      setShowForm(false); fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    try { await axios.delete(`${API_URL}/api/opportunities/${id}`, { withCredentials: true }); toast.success('Supprime'); fetchItems(); }
    catch { toast.error('Erreur'); }
  };
  const handleStatusChange = async (id, newStatus) => {
    try { await axios.put(`${API_URL}/api/opportunities/${id}`, { status: newStatus }, { withCredentials: true }); toast.success('Statut mis a jour'); fetchItems(); }
    catch { toast.error('Erreur'); }
  };

  const currentType = TYPES.find(t => t.key === activeType);

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div data-testid="opportunities-page">
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Megaphone size={26} weight="bold" /> Appel d'offre</h1>
        <p className="page-subtitle">{currentType?.description}</p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: 8 }}>
        <LayoutToggle layout={layoutMode} setLayout={setLayoutMode} testidPrefix="opp-layout" />
        {isAdmin && <button className="btn btn-primary" onClick={openCreate} data-testid="add-opp-btn"><Plus size={16} /> Ajouter</button>}
      </div>

      {items.length === 0 ? (
        <div className="card" style={{ padding: '48px', textAlign: 'center', color: '#5E5F66' }}><currentType.icon size={48} style={{ opacity: 0.4, marginBottom: '12px' }} /><p>Aucune entree</p></div>
      ) : (
        (() => {
          const lp = getLayoutContainerProps(layoutMode, { cardMinWidth: '320px' });
          return (
            <div style={lp.style} className={lp.className || ''} data-testid={`opp-container-${layoutMode}`}>
          {items.map(o => {
            const st = STATUS_CONFIG[o.status] || STATUS_CONFIG.en_cours;
            const isOpen = openCardId === o.id;
            return (
              <div
                key={o.id}
                data-testid={`opp-card-${o.id}`}
                className="card"
                style={{
                  padding: 0,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  borderLeft: `4px solid ${st.color}`,
                  transition: 'all 0.2s ease',
                  boxShadow: isOpen ? '0 8px 24px rgba(0,47,167,0.15)' : '0 1px 3px rgba(0,0,0,0.06)',
                }}
                onClick={() => setOpenCardId(isOpen ? null : o.id)}
              >
                <div style={{ padding: '16px 18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, gap: 8 }}>
                    <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#1F2937', fontWeight: 700, lineHeight: 1.3 }}>{o.title}</h3>
                    <span style={{ padding: '3px 10px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 700, backgroundColor: st.bg, color: st.color, whiteSpace: 'nowrap' }}>{st.label}</span>
                  </div>
                  {o.organization && (
                    <div style={{ fontSize: '0.85rem', color: '#5E5F66', marginBottom: 4 }}>
                      <strong style={{ color: '#1F2937' }}>{o.organization}</strong>
                    </div>
                  )}
                  {o.reference && (
                    <div style={{ fontSize: '0.78rem', color: '#9CA3AF', marginBottom: 8 }}>Réf. {o.reference}</div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 10, borderTop: '1px solid #F4F5F7' }}>
                    <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#002FA7' }}>
                      {(o.estimated_amount || 0).toLocaleString('fr-FR')} €
                    </div>
                    {(o.deadline || o.target_date) && (
                      <div style={{ fontSize: '0.78rem', color: '#5E5F66', textAlign: 'right' }}>
                        <div style={{ color: '#9CA3AF', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>Date limite</div>
                        <strong style={{ color: '#1F2937' }}>{fmtDate(o.deadline || o.target_date)}</strong>
                      </div>
                    )}
                  </div>
                  {o.client_name && (
                    <div style={{ fontSize: '0.82rem', color: '#5E5F66', marginTop: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <UserCircle size={14} /> {o.client_name}
                    </div>
                  )}
                </div>

                {isOpen && (
                  <div style={{ padding: '14px 18px', borderTop: '1px solid #E2E2E6', background: '#F8FAFC' }} onClick={(e) => e.stopPropagation()} data-testid={`opp-card-expanded-${o.id}`}>
                    {o.notes && (
                      <div style={{ fontSize: '0.85rem', color: '#374151', marginBottom: 10, padding: 8, background: 'white', borderRadius: 4, whiteSpace: 'pre-wrap' }}>{o.notes}</div>
                    )}
                    {isAdmin && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => setTrackOpp(o)} data-testid={`opp-track-${o.id}`} style={{ color: '#0369A1' }} title="Suivi détaillé"><ListChecks size={14} /> Suivi du dossier</button>
                        {o.status === 'en_cours' && (
                          <button className="btn btn-secondary btn-sm" onClick={() => handleStatusChange(o.id, 'en_nego')} style={{ color: '#EF6C00' }} data-testid={`opp-nego-${o.id}`}><Handshake size={14} /> En négo</button>
                        )}
                        {(o.status === 'en_cours' || o.status === 'en_nego') && (
                          <>
                            <button className="btn btn-secondary btn-sm" onClick={() => handleStatusChange(o.id, 'gagnee')} style={{ color: '#2E7D32' }} data-testid={`opp-win-${o.id}`}><CheckCircle size={14} /> Gagnée</button>
                            <button className="btn btn-secondary btn-sm" onClick={() => handleStatusChange(o.id, 'perdue')} style={{ color: '#C62828' }} data-testid={`opp-lose-${o.id}`}><XCircle size={14} /> Perdue</button>
                          </>
                        )}
                        <button className="btn btn-secondary btn-sm" onClick={() => openEdit(o)} data-testid={`opp-edit-${o.id}`}><PencilSimple size={14} /> Modifier</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(o.id)} data-testid={`opp-delete-${o.id}`}><Trash size={14} /></button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
          );
        })()
      )}

      {showForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '6px', width: '100%', maxWidth: '580px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0 }}>{editing ? 'Modifier' : `Nouvelle ${currentType?.label.toLowerCase()}`}</h3>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
              <div className="form-group"><label className="form-label">Titre *</label><input type="text" className="form-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
              {form.type === 'appel_offres' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group"><label className="form-label">Organisme</label><input type="text" className="form-input" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} /></div>
                  <div className="form-group"><label className="form-label">Reference AO</label><input type="text" className="form-input" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
                </div>
              )}
              {form.type === 'prospect' && (
                <div className="form-group"><label className="form-label">Source du prospect</label><input type="text" className="form-input" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Bouche a oreille, site web, ..." /></div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <div className="form-group"><label className="form-label">Contact</label><input type="text" className="form-input" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></div>
                <div className="form-group"><label className="form-label">Email</label><input type="email" className="form-input" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} /></div>
                <div className="form-group"><label className="form-label">Telephone</label><input type="tel" className="form-input" value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <div className="form-group"><label className="form-label">Montant estime</label><input type="number" step="0.01" className="form-input" value={form.estimated_amount} onChange={(e) => setForm({ ...form, estimated_amount: parseFloat(e.target.value) || 0 })} /></div>
                {form.type === 'appel_offres' ? (
                  <div className="form-group"><label className="form-label">Date limite</label><input type="date" className="form-input" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} /></div>
                ) : (
                  <div className="form-group"><label className="form-label">Date cible</label><input type="date" className="form-input" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} /></div>
                )}
                <div className="form-group"><label className="form-label">Statut</label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="z-[9999]">
                      {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="form-group"><label className="form-label">Notes</label><textarea className="form-input" rows="3" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowForm(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{editing ? 'Enregistrer' : 'Creer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {trackOpp && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: 30, overflow: 'auto' }} onClick={(e) => e.target === e.currentTarget && setTrackOpp(null)}>
          <div style={{ background: 'white', borderRadius: 10, width: '100%', maxWidth: 920, padding: 22 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: '0.78rem', color: '#5E5F66', textTransform: 'uppercase', fontWeight: 600 }}>Suivi appel d'offres</div>
                <h3 style={{ margin: '4px 0 0 0', fontSize: '1.1rem', fontWeight: 700 }}>{trackOpp.title}</h3>
                <div style={{ fontSize: '0.82rem', color: '#5E5F66' }}>
                  {trackOpp.organization && <span>{trackOpp.organization}</span>}
                  {trackOpp.deadline && <span> · Date limite : {fmtDateFR(trackOpp.deadline)}</span>}
                </div>
              </div>
              <button onClick={() => setTrackOpp(null)} className="btn btn-secondary btn-sm"><X size={14} /></button>
            </div>
            <TenderTrackingPanel opportunityId={trackOpp.id} />
          </div>
        </div>
      )}
    </div>
  );
};

export default OpportunitiesPage;
